import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, CurrencyDollar, Storefront } from "@phosphor-icons/react";
import { format, parseISO } from "date-fns";
import { TopHeader } from "@/components/TopHeader";

export default function SalesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedCustomer = searchParams.get("customer");

  const [sales, setSales] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({ total: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    customer_id: preselectedCustomer || "",
    amount: "",
    description: "",
  });

  useEffect(() => {
    fetchData();
    if (preselectedCustomer) {
      setDialogOpen(true);
    }
  }, []);

  const fetchData = async () => {
    try {
      const [salesRes, customersRes, statsRes] = await Promise.all([
        axios.get(`${API}/sales`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/sales/stats`),
      ]);
      setSales(salesRes.data);
      setCustomers(customersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to load sales data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id) {
      toast.error("Please select a customer");
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/sales`, {
        customer_id: formData.customer_id,
        amount: parseFloat(formData.amount),
        description: formData.description || null,
        sale_date: new Date().toISOString(),
      });
      toast.success("Sale recorded successfully");
      setDialogOpen(false);
      setFormData({ customer_id: "", amount: "", description: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record sale");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "MMM d, yyyy") + " IST";
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="sales-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0055FF]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="sales-page">
      {/* Header */}
      <TopHeader title="Sales" subtitle="TRACKING">
        <Button
          onClick={() => setDialogOpen(true)}
          className="h-10 px-4 bg-[#0055FF] hover:bg-[#0040CC] text-white font-semibold rounded-full"
          data-testid="add-sale-button"
        >
          <Plus size={20} weight="bold" className="mr-1" />
          Add
        </Button>
      </TopHeader>

      <div className="p-4 space-y-4">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="stat-card animate-fade-in-up" data-testid="total-sales-stat">
            <div className="flex items-center gap-2 mb-2">
              <CurrencyDollar size={24} className="text-[#10B981]" weight="fill" />
            </div>
            <p className="stat-value">${stats.total.toLocaleString()}</p>
            <p className="stat-label">Total Sales</p>
          </div>
          <div className="stat-card animate-fade-in-up stagger-1" data-testid="sales-count-stat">
            <p className="stat-value">{stats.count}</p>
            <p className="stat-label">Transactions</p>
          </div>
        </div>

        {/* Sales List */}
        <div className="animate-fade-in-up stagger-2">
          <h2 className="font-bold text-[#0A0A0A] mb-3" style={{ fontFamily: "'Chivo', sans-serif" }}>
            Recent Sales
          </h2>

          {sales.length > 0 ? (
            <div className="card-flat overflow-hidden">
              {sales.map((sale, index) => (
                <div
                  key={sale.id}
                  className="list-item cursor-pointer"
                  onClick={() => navigate(`/customers/${sale.customer_id}`)}
                  data-testid={`sale-item-${index}`}
                >
                  <div className="w-10 h-10 rounded-full bg-[#D1FAE5] flex items-center justify-center mr-3">
                    <Storefront size={20} className="text-[#10B981]" weight="fill" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-[#0A0A0A]">{sale.customer_name}</p>
                    <p className="text-sm text-[#4B5563]">
                      {formatDate(sale.sale_date)}
                      {sale.description && ` • ${sale.description}`}
                    </p>
                  </div>
                  <p className="font-bold text-[#10B981]" style={{ fontFamily: "'Chivo', sans-serif" }}>
                    ${sale.amount.toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state card-flat" data-testid="sales-empty">
              <CurrencyDollar size={48} className="text-[#D1D5DB] mb-2" />
              <h3 className="empty-state-title">No Sales Yet</h3>
              <p className="empty-state-text">Record your first sale to start tracking</p>
              <Button
                onClick={() => setDialogOpen(true)}
                className="mt-4 bg-[#0055FF] hover:bg-[#0040CC] text-white font-semibold rounded-full"
                data-testid="add-first-sale-button"
              >
                <Plus size={20} weight="bold" className="mr-1" />
                Add Sale
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Add Sale Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Sale</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-[#0A0A0A] font-medium">Customer *</Label>
              <Select
                value={formData.customer_id}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, customer_id: value }))
                }
              >
                <SelectTrigger className="mt-1.5 h-12 border-[#E5E7EB]" data-testid="sale-customer-select">
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="amount" className="text-[#0A0A0A] font-medium">
                Amount ($) *
              </Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, amount: e.target.value }))
                }
                placeholder="0.00"
                className="mt-1.5 h-12 border-[#E5E7EB]"
                data-testid="sale-amount-input"
              />
            </div>

            <div>
              <Label htmlFor="description" className="text-[#0A0A0A] font-medium">
                Description (Optional)
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, description: e.target.value }))
                }
                placeholder="What was sold..."
                rows={2}
                className="mt-1.5 border-[#E5E7EB] resize-none"
                data-testid="sale-description-input"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="flex-1"
                data-testid="cancel-sale-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-[#0055FF] hover:bg-[#0040CC] text-white"
                data-testid="submit-sale-button"
              >
                {submitting ? "Saving..." : "Save Sale"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
